import { useState, useEffect, useRef } from "react";
import { useAtom } from "jotai";
import MGAAPI from "../../api/index";
import displayError from "../../constants/utils";
import "../sideMenu/style.scss";
import { componentsAtom } from "../../utils/atoms/componentsAtom";
import LoadAttributesForm from "../loadAttributesForm";
import fileDownload from "js-file-download";
import { useAssessmentRouteParams, useCurrentDesignComponentLists, updatedUnits } from "../../constants/helper";
import { saveDisabledAtom } from "../../utils/atoms/saveDisabledAtom";
import _ from "lodash";


const ElectricLoad = ({
  componentData,
  componentDataThermal,
  form,
  updateLoadState,
  componentDetails,
  setLoadDataSet,
  handleLoadUpload,
  projectDetails,
  setLoadingComp
}) => {
  const MGA_API = new MGAAPI();
  const {designId,designVersionId} = useAssessmentRouteParams();
  const selectedDesignAndDesignVersion = useCurrentDesignComponentLists(
    projectDetails?.designComponentsList
  );
  const [saveDisabled, setSaveDisabled] = useAtom(saveDisabledAtom);
  const [dataAssetElectric, setDataAssetElectric] = useState(
    componentData?.componentAttributes?.loadParameters?.profileFile || []
  );
  const [dataAssetThermal, setDataAssetThermal] = useState(
    componentDataThermal?.componentAttributes?.loadParameters?.profileFile || []
  );
  const [electricalLoadFileContent, setElectricalLoadFileContent] =
    useState("");
  const [thermalLoadFileContent, setThermalLoadFileContent] = useState("");

  const [projectComponents] = useAtom(componentsAtom);

  const [displayLoader, setDisplayLoader] = useState(false);
  const [previousLoadData, setPreviousLoadData] = useState({
    startDate: componentDetails?.load?.componentAttributes?.loadParameters
      ?.profileDetail?.startDate
      ? new Date(
          componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail?.startDate
        )
      : new Date(
          new Date(new Date().setDate(new Date().getDate() - 1)).setFullYear(
            new Date().getFullYear() - 1
          )
        ),
    startTime:
      componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail
        ?.startTime,
    timeResolution:
      componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail
        ?.timeResolution,
    powerFactor:
      componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail
        ?.powerFactor,    
  });

  const newLoadData = {
    startDate:
      componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail
        ?.startDate,
    startTime:
      componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail
        ?.startTime,
    timeResolution:
      componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail
        ?.timeResolution,
    powerFactor:
      componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail
        ?.powerFactor,
  };

  const previousUnitsRef = useRef(
    componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail
      ?.units
  );

  // Clear graph data and related profile files when units change
  useEffect(() => {
    const currentUnits =
      componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail
        ?.units;
    if (
      previousUnitsRef.current &&
      currentUnits &&
      previousUnitsRef.current !== currentUnits
    ) {
      // Clear displayed datasets
      setDataAssetElectric([]);
      setDataAssetThermal([]);
      // Clear load data set used elsewhere
      if (typeof setLoadDataSet === "function") {
        setLoadDataSet({ electrical: [], thermal: [] });
      }
      // Clear profile files and related flags in component state
      updateLoadState(
        "componentAttributes.loadParameters",
        "profileFile",
        []
      );
      updateLoadState(
        "componentAttributes.loadParameters.profileDetail",
        "loadFile",
        null
      );
      updateLoadState(
        "componentAttributes.loadParameters.profileDetail",
        "thermalFile",
        null
      );
      updateLoadState(
        "componentAttributes.loadParameters.profileDetail",
        "isLoadFileClicked",
        false
      );
      updateLoadState(
        "componentAttributes.loadParameters.profileDetail",
        "isThermalFileClicked",
        false
      );
    }
    previousUnitsRef.current = currentUnits;
  }, [
    componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail?.units,
  ]);

  const checkIfDataChanged = (data) => {
    return JSON.stringify(data) !== JSON.stringify(previousLoadData);
  };

  useEffect(() => {
    if (checkIfDataChanged(newLoadData)) {
      updateLoadState(
        "componentAttributes.loadParameters.profileDetail",
        "loadFile",
        null
      );
      setDataAssetElectric([]);
      setDataAssetThermal([]);
      updateLoadState(
        "componentAttributes.loadParameters.profileDetail",
        "isLoadFileClicked",
        false
      );
      updateLoadState("componentAttributes.loadParameters", "profileFile", []);
      setPreviousLoadData(newLoadData);
      setLoadDataSet({
        electrical: [],
        thermal: [],
      });
      updateLoadState(
        "componentAttributes.loadParameters.profileDetail",
        "useThermalLoad",
        false
      );
    }
  }, [newLoadData, designVersionId]);

  const startDate =
    projectComponents?.load?.componentAttributes?.loadParameters?.profileDetail
      ?.startDate;

  const handleGetProfileClick = async () => {
    const isThermalEnabled =
      componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail
        ?.useThermalLoad;
    if (!isThermalEnabled && !electricalLoadFileContent) {
      return;
    }
    if (isThermalEnabled && !thermalLoadFileContent) {
      return;
    }
    setDisplayLoader(true);
    const payload = {
      startDate:
        componentDetails?.load?.componentAttributes?.loadParameters
          ?.profileDetail?.startDate,
      startTime:
        componentDetails?.load?.componentAttributes?.loadParameters
          ?.profileDetail?.startTime,
      componentType: componentDetails?.load?.componentAttributes?.loadParameters
        ?.profileDetail?.useThermalLoad
        ? "ThermalLoad"
        : "ElectricalLoad",
      units:
        componentDetails?.load?.componentAttributes?.loadParameters
          ?.profileDetail?.units,
      timeResolution:
        componentDetails?.load?.componentAttributes?.loadParameters
          ?.profileDetail?.timeResolution || "15",
      datasource: "Upload",
      powerFactor:
        componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail
          ?.powerFactor,
    };
    const formData = new FormData();
    formData.append(
      "file",
      !componentDetails?.load?.componentAttributes?.loadParameters
        ?.profileDetail?.useThermalLoad
        ? componentDetails?.load?.componentAttributes?.loadParameters
            ?.profileDetail?.loadFile
        : componentDetails?.load?.componentAttributes?.loadParameters
            ?.profileDetail?.thermalFile
    );
    formData.append(
      "profileDetail",
      new Blob([JSON.stringify(payload)], { type: "application/json" })
    );

    try {
      setSaveDisabled(true);
      await MGA_API.getProfile(formData).then((res) => {
        if (
          !componentDetails?.load?.componentAttributes?.loadParameters
            ?.profileDetail?.useThermalLoad &&
          componentDetails?.load?.componentAttributes?.loadParameters
            ?.profileDetail?.loadFile
        ) {
          setDataAssetElectric(res);
          updateLoadState(
            "componentAttributes.loadParameters",
            "profileFile",
            res
          );
          handleLoadUpload();
        } else if (
          componentDetails?.load?.componentAttributes?.loadParameters
            ?.profileDetail?.useThermalLoad
        ) {
          setDataAssetElectric(
            componentData?.componentAttributes?.loadParameters?.profileFile
          );
          setDataAssetThermal([...res]);
          updateLoadState(
            "componentAttributes.loadParameters",
            "profileFile",
            res
          );
        }
      });
     if(updatedUnits?.includes(componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail?.units)) {
        updateLoadState(
           "componentAttributes.loadParameters.profileDetail",
           "units",
           'kW'
        );
      }
      setSaveDisabled(false);
      setDisplayLoader(false);
      displayError("success", "File uploaded successfully!");
    } catch (error) {
      if (
        componentDetails?.load?.componentAttributes?.loadParameters
          ?.profileDetail?.useThermalLoad
      ) {
        setLoadDataSet((prev) => ({
          ...prev,
          thermal: [],
        }));
        // Clear thermal file on error
        updateLoadState(
          "componentAttributes.loadParameters.profileDetail",
          "thermalFile",
          null
        );
        setThermalLoadFileContent("");
      } else {
        setLoadDataSet((prev) => ({
          ...prev,
          electrical: [],
        }));
        // Clear electrical file on error
        updateLoadState(
          "componentAttributes.loadParameters.profileDetail",
          "loadFile",
          null
        );
        setElectricalLoadFileContent("");
      }
      setDisplayLoader(false);
      displayError("error", error?.response?.data?.message || "Error occurred while uploading file");
    }
  };

  useEffect(() => {
    setDataAssetElectric(
      componentData?.componentAttributes?.loadParameters?.profileFile || componentDetails?.load?.componentAttributes?.loadParameters?.profileFile || []
    );
    setDataAssetThermal(
      componentDataThermal?.componentAttributes?.loadParameters?.profileFile ||
        []
    );
  }, [componentData, componentDataThermal, designVersionId]);

  const isThermalEnabled =
    componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail
      ?.useThermalLoad;

  useEffect(() => {
    if(isThermalEnabled) {
      form.validateField('ThermalEmissionFactor');
      form.validateField('ThermalEnergyPrice');
    }
    if (!isThermalEnabled && dataAssetElectric?.length > 0) {
      setSaveDisabled(false);
    }
  }, [isThermalEnabled]);

  const handleFileInputChange = (file, fileType) => {
    if (!file) return;
    if (fileType === "loadFile") {
      setElectricalLoadFileContent(file);
      setLoadDataSet((prev) => ({
        ...prev,
        electrical: [],
      }));
    } else {
      setThermalLoadFileContent(file);
      setLoadDataSet((prev) => ({
        ...prev,
        thermal: [],
      }));
    }
    updateLoadState(
      "componentAttributes.loadParameters.profileDetail",
      fileType,
      file
    );
  };

  const handleFileDownload = (type) => {
    setLoadingComp(true);
    MGA_API.downloadImportTemplate(type)
      .then(({ data, fileName }) => {
        fileDownload(data, fileName || "LoadFile");
        displayError("success", "File Downloaded successfully!");
      })
      .catch((error) => {
        console.log("Error Downloading File:", error);
      })
      .finally(() => {
        setLoadingComp(false);
      });
  };

  return (
    <>
      <se-loading loading={displayLoader} />
      <LoadAttributesForm
        handleFileInputChange={handleFileInputChange}
        startDate={startDate}
        updateLoadState={updateLoadState}
        dataAssetElectric={dataAssetElectric}
        dataAssetThermal={dataAssetThermal}
        componentData={componentData}
        handleFileClick={handleGetProfileClick}
        form={form}
        loadParameters={componentDetails?.load}
        handleFileDownload={handleFileDownload}
      />
    </>
  );
};
export default ElectricLoad;
